import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import PATHS from 'lib/navigation/paths';
import {
  RiChat3Line,
  RiLoginBoxLine,
  RiLogoutBoxLine,
  RiUserAddLine,
  RiCheckLine,
  RiUserSettingsLine,
  RiGiftLine,
} from 'react-icons/ri';
import {
  useMediaQuery, useTheme, Avatar, Typography,
} from '@mui/material';
import axios, { API } from 'lib/config/axios';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import useNavigationItems from 'views/rebranded-module/common/hooks/useNavigationItems';
import Reservation from 'views/rebranded-module/common/icons/Reservation';
import { DROPDOWN_REF } from 'views/rebranded-module/common/utils/constants';
import { MANAGEABLE_MEMBERSHIP_STATUSES } from 'views/user-module/UserMembershipV2View/utils/membershipStatus'; // ADD THIS

const useDropdownMenuItems = (simple, reservationPath) => {
  const { t } = useTranslation();
  const appState = useSelector((state) => state.globalData.appState);
  const user = useSelector((state) => state.globalData.appState.current_user);
  const isPrivileged = useSelector(
    (state) => state.globalData.appState.current_user?.privileged,
  );
  const handleErrorResponse = useErrorsHandler();
  const appNavigator = useAppNavigator();
  const navigationItems = useNavigationItems(simple);
  const theme = useTheme();
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('xl'));

  const showManageMembership = appState.user_membership
    && MANAGEABLE_MEMBERSHIP_STATUSES.includes(
      appState.user_membership?.status?.sym_id,
    );

  const handleSignOut = async (e) => {
    e.preventDefault();
    try {
      await axios.get(API.users.signOut);
      localStorage.setItem('signout', true);
      if (typeof ReactNativeWebView !== 'undefined') {
        // eslint-disable-next-line no-undef
        ReactNativeWebView.postMessage(
          JSON.stringify({
            type: 'login',
            data: { token: null, current_user: {} },
          }),
        );
      }
      appNavigator.push('/');
    } catch (error) {
      handleErrorResponse(error);
    }
  };
  const dropdownMenuItems = useMemo(() => {
    const dropdownMenuUtm = {
      ref: DROPDOWN_REF,
      source: appNavigator.pathname,
    };
    const currentUrl = typeof window === 'undefined' ? null : window.location.href;

    // Initialize items array with dividers
    let itemsWithDividers = [];

    // Group 1: User profile or Sign In
    if (user) {
      itemsWithDividers = itemsWithDividers.concat([
        {
          label: `${user.first_name} ${user.last_name}`,
          path: PATHS.user.dashboard(dropdownMenuUtm),
          userName: true,
          subLable: t('common_layout_v2:view_account'),
          icon: (
            <Avatar className="UDAvatar" src={user.avatar_url}>
              <Typography component="span" className="UDMFisrtLitter">
                {user.first_name.charAt(0).toUpperCase()}
              </Typography>
            </Avatar>
          ),
        },
      ]);
    } else {
      itemsWithDividers = itemsWithDividers.concat([
        {
          label: t('common_layout_v2:log_in'),
          path: PATHS.users.signIn({ return_url: currentUrl }),
          testId: 'nav-login',
          icon: <RiLoginBoxLine />,
        },
        {
          label: t('common_layout_v2:sign_up'),
          path: PATHS.users.signUp({ return_url: currentUrl }),
          testId: 'nav-signup',
          icon: <RiUserAddLine />,
        },
      ]);
    }

    // Group 2: Reservation and manage Club
    const group2Items = [];
    if (user && !isPrivileged) {
      group2Items.push({
        label: t('common_layout_v2:my_reservations'),
        path: PATHS.user.vehicleReservations(dropdownMenuUtm),
        icon: <Reservation />,
        testId: 'nav-my-reservations',
      });
    }
    if (showManageMembership && !isPrivileged) {
      group2Items.push({
        label: t('common_layout_v2:navigation.manage_membership'),
        path: PATHS.user.membership(dropdownMenuUtm),
        icon: <RiUserSettingsLine />,
        testId: 'nav-manage-membership',
      });
    }

    // Group 3: Navigation items
    const group3Items = [];
    if (!reservationPath && (simple || !isLargeDesktop)) {
      group3Items.push(...navigationItems);
    }

    if (!reservationPath) {
      group3Items.push(
        {
          label: t('common_layout_v2:gift_ride'),
          path: PATHS.motorcycleRentalGiftCards.buy(dropdownMenuUtm),
          icon: <RiGiftLine />,
          testId: 'nav-gift-cards',
        },
        {
          label: t('common_layout_v2:checkin'),
          path: PATHS.checkIn(dropdownMenuUtm),
          icon: <RiCheckLine />,
          testId: 'nav-check_in',
        },
      );
    }

    if (group2Items.length > 0 || group3Items.length > 0) {
      itemsWithDividers.push({ isDivider: true });
    }

    if (group2Items.length > 0) {
      itemsWithDividers = itemsWithDividers.concat(group2Items);
      itemsWithDividers.push({ isDivider: true });
    }

    if (group3Items.length > 0) {
      itemsWithDividers = itemsWithDividers.concat(group3Items);
      // Add divider after group 3
      itemsWithDividers.push({ isDivider: true });
    }

    // Group 4: contact us and log out
    const group4Items = [];
    if (!reservationPath) {
      group4Items.push({
        label: t('common_layout_v2:contact_us'),
        path: PATHS.contactUs(dropdownMenuUtm),
        icon: <RiChat3Line />,
      });
    }
    if (user) {
      group4Items.push({
        label: t('common_layout_v2:log_out'),
        icon: <RiLogoutBoxLine />,
        onClick: handleSignOut,
        testId: 'nav-logout',
      });
    }

    if (group4Items.length > 0) {
      itemsWithDividers = itemsWithDividers.concat(group4Items);
    }

    return itemsWithDividers;
  }, [
    navigationItems,
    isLargeDesktop,
    simple,
    reservationPath,
    user,
    typeof window !== 'undefined' && window?.location?.href,
    showManageMembership,
    appState.user_membership?.status.sym_id,
  ]);

  return dropdownMenuItems;
};

export default useDropdownMenuItems;
